import { useCallback, useState } from 'react';
import emailDraftsService from 'services/emailDraftsService';

/**
 * Generates an AI-drafted LP outreach email and owns the request state, so
 * consumers only deal with presentation.
 *
 * @returns {Object} { generate, apply, reset, draft, loading, error }
 */
const useGenerateDraft = () => {
	const [draft, setDraft] = useState(null);
	const [generateLoading, setGenerateSubmission] = useState(false);
	const [applyEditsLoading, setApplyEditsLoading] = useState(false);
	const [error, setError] = useState(null);

	const generate = useCallback(
		async ({ tenantId, topic, salesforceObjectType, salesforceObjectId }) => {
			setGenerateSubmission(true);
			setError(null);

			try {
				const result = await emailDraftsService.generateDraft(tenantId, {
					topic,
					salesforceObjectType,
					salesforceObjectId,
				});

				setDraft(result);

				return result;
			} catch (err) {
				// emailDraftsService already produces user-safe messages. Rethrow so the
				// caller can surface it however it presents errors.
				setError(err.message);

				throw err;
			} finally {
				setGenerateSubmission(false);
			}
		},
		[]
	);

	// Apply a quick edit (preset type or custom instruction) to the existing draft.
	const apply = useCallback(
		async ({ tenantId, presetTypes, customInstruction }) => {
			setApplyEditsLoading(true);
			setError(null);

			try {
				if (!draft?.email) {
					throw new Error('No draft to apply quick edit to');
				}

				const result = await emailDraftsService.quickEdit(tenantId, {
					subject: draft.email.subject,
					body: draft.email.body,
					preset_types: presetTypes,
					custom_instruction: customInstruction,
				});

				setDraft((prev) => (prev ? { ...prev, email: result.email } : prev));

				return result;
			} catch (err) {
				setError(err.message);

				throw err;
			} finally {
				setApplyEditsLoading(false);
			}
		},
		[draft]
	);

	// Clears a draft that no longer matches the user's selection.
	const reset = useCallback(() => {
		setDraft(null);
		setError(null);
	}, []);

	return { generate, apply, reset, draft, generateLoading, applyEditsLoading, error };
};

export default useGenerateDraft;
